import { useState } from "react";
import { Cake, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const formatDate = (iso) =>
  new Date(iso + "T12:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

const birthdayInfo = (iso) => {
  const birth = new Date(iso + "T12:00:00");
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const next = new Date(today.getFullYear(), birth.getMonth(), birth.getDate());
  if (next < new Date(today.getFullYear(), today.getMonth(), today.getDate())) next.setFullYear(today.getFullYear() + 1);
  else if (!(next.getMonth() === today.getMonth() && next.getDate() === today.getDate())) age -= 1;
  const days = Math.round((next - new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000);
  return { age: Math.max(age, 0), days };
};

export const DogBirthday = ({ dogName, birthDate, canEdit, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(birthDate || "");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/dogs/${encodeURIComponent(dogName)}/birthday`, { birth_date: value });
      toast.success(`Compleanno di ${dogName} salvato: gli auguri partiranno in automatico.`);
      onSaved(data.birth_date);
      setEditing(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!birthDate && !canEdit) return null;
  const info = birthDate ? birthdayInfo(birthDate) : null;

  return (
    <div data-testid="dog-birthday" className="mt-6 inline-flex flex-col gap-3 rounded-2xl border border-[#E8DFD5] bg-white px-5 py-4">
      {birthDate && !editing ? (
        <div className="flex flex-wrap items-center gap-3 text-sm text-[#5C534B]">
          <Cake size={16} className="text-[#B85328]" />
          <span data-testid="dog-birthday-text">
            {info.days === 0 ? (
              <strong className="text-[#B85328]">Oggi {dogName} compie {info.age} {info.age === 1 ? "anno" : "anni"}!</strong>
            ) : (
              <>
                Nato il {formatDate(birthDate)} · {info.age} {info.age === 1 ? "anno" : "anni"} · compleanno tra {info.days} {info.days === 1 ? "giorno" : "giorni"}
              </>
            )}
          </span>
          {canEdit && (
            <button data-testid="dog-birthday-edit" onClick={() => setEditing(true)} className="text-[#8C7B6E] transition-colors hover:text-[#B85328]" aria-label="Modifica data di nascita">
              <Pencil size={14} />
            </button>
          )}
        </div>
      ) : (
        <form data-testid="dog-birthday-form" onSubmit={save} className="flex flex-wrap items-center gap-3">
          <label htmlFor="birth-date" className="flex items-center gap-2 text-sm text-[#5C534B]">
            <Cake size={16} className="text-[#B85328]" /> Data di nascita di {dogName}
          </label>
          <input
            id="birth-date"
            data-testid="dog-birthday-input"
            type="date"
            required
            value={value}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setValue(e.target.value)}
            className="rounded-xl border border-[#E8DFD5] bg-[#FBF8F3] px-3 py-2 text-sm text-[#2C2723] outline-none focus:border-[#B85328]"
          />
          <button type="submit" data-testid="dog-birthday-save" disabled={busy} className="rounded-full bg-[#B85328] px-5 py-2 text-xs font-semibold text-[#FBF8F3] transition-all hover:bg-[#9C421C] disabled:opacity-60">
            Salva
          </button>
          {birthDate && (
            <button type="button" data-testid="dog-birthday-cancel" onClick={() => setEditing(false)} className="text-xs font-semibold text-[#8C7B6E] hover:text-[#2C2723]">
              Annulla
            </button>
          )}
        </form>
      )}
    </div>
  );
};
